/**
 * useRoutePersist Hook
 *
 * Persists GPX route data to the backend by uploading the raw GPX file
 * to POST /experiences/:id/routes/gpx when experienceId + GPX data are available.
 *
 * The backend RouteService handles all parsing, simplification, PostGIS conversion,
 * and metric unit storage internally.
 *
 * Epic: MVP 1.20.6-S - Key Locations Guided Editor
 * Story: SCRUM-830 (FIX-F08a: keyLocations save flow)
 */

import { useState, useCallback, useRef } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

/**
 * useRoutePersist
 *
 * Uploads a GPX file to persist route data for an experience.
 * Handles delete-then-recreate for existing routes (immutable pattern).
 *
 * @param {string|null} experienceId - Experience UUID (null if not yet created)
 * @returns {Object} Persist state and methods
 */
export default function useRoutePersist(experienceId) {
  const [isPersisting, setIsPersisting] = useState(false);
  const [persistError, setPersistError] = useState(null);
  const [hasPersisted, setHasPersisted] = useState(false);
  const [persistedRouteId, setPersistedRouteId] = useState(null);

  // Track what we've already persisted to avoid duplicate uploads
  const lastPersistedXml = useRef(null);

  /**
   * Build fetch config with auth headers for multipart upload
   */
  const getAuthHeaders = useCallback(() => {
    const headers = {};
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    return headers;
  }, []);

  /**
   * Delete existing route for this experience (immutable pattern — AC-02)
   *
   * @param {number} routeId - Route ID to delete
   */
  const deleteExistingRoute = useCallback(async (routeId) => {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/routes/${routeId}`,
      {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      }
    );

    if (!response.ok && response.status !== 404) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Delete failed: ${response.status}`);
    }
  }, [getAuthHeaders]);

  /**
   * Check for existing routes and get their IDs
   *
   * @param {string} expId - Experience ID
   * @returns {Promise<Array>} Existing route IDs
   */
  const getExistingRouteIds = useCallback(async (expId) => {
    const response = await fetch(
      `${API_BASE_URL}/api/v1/experiences/${expId}/routes`,
      {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      }
    );

    if (!response.ok) {
      return [];
    }

    const result = await response.json();
    if (result.success && Array.isArray(result.data)) {
      return result.data.map(r => r.id);
    }
    return [];
  }, [getAuthHeaders]);

  /**
   * Persist GPX route data to the backend
   *
   * Creates a File from raw GPX XML and uploads via POST /experiences/:id/routes/gpx.
   * If a route already exists, deletes it first (immutable pattern — AC-02).
   *
   * @param {string} gpxRawXml - Raw GPX XML content
   * @param {string} filename - Original GPX filename
   * @returns {Promise<Object|null>} Created route data or null on failure
   */
  const persistRoute = useCallback(async (gpxRawXml, filename) => {
    if (!experienceId || experienceId === 'new') {
      setPersistError('Experience must be saved before route can be persisted');
      return null;
    }

    if (!gpxRawXml) {
      setPersistError('No GPX data to persist');
      return null;
    }

    // Skip if we already persisted this exact GPX content
    if (lastPersistedXml.current === gpxRawXml) {
      return null;
    }

    setIsPersisting(true);
    setPersistError(null);

    try {
      // AC-02: Delete existing routes before re-upload (immutable pattern)
      const existingIds = await getExistingRouteIds(experienceId);
      for (const routeId of existingIds) {
        await deleteExistingRoute(routeId);
      }

      // Create a File object from the raw GPX XML
      const gpxFile = new File(
        [gpxRawXml],
        filename || 'route.gpx',
        { type: 'application/gpx+xml' }
      );

      // Build multipart form data
      const formData = new FormData();
      formData.append('file', gpxFile);

      // Upload GPX file — AC-01, AC-03 (raw GPX included)
      const response = await fetch(
        `${API_BASE_URL}/api/v1/experiences/${experienceId}/routes/gpx`,
        {
          method: 'POST',
          body: formData,
          headers: getAuthHeaders(),
          credentials: 'include'
          // Note: Don't set Content-Type — browser sets multipart boundary
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Upload failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Route persist failed');
      }

      // Track successful persist
      lastPersistedXml.current = gpxRawXml;
      setPersistedRouteId(result.data.id);
      setHasPersisted(true);
      setIsPersisting(false);

      return result.data;
    } catch (err) {
      console.error('[useRoutePersist] Persist error:', err);
      setPersistError(err.message || 'Failed to persist route');
      setIsPersisting(false);
      return null;
    }
  }, [experienceId, getExistingRouteIds, deleteExistingRoute, getAuthHeaders]);

  /**
   * Reset persist state
   */
  const resetPersist = useCallback(() => {
    setIsPersisting(false);
    setPersistError(null);
    setHasPersisted(false);
    setPersistedRouteId(null);
    lastPersistedXml.current = null;
  }, []);

  return {
    // State
    isPersisting,
    persistError,
    hasPersisted,
    persistedRouteId,

    // Methods
    persistRoute,
    resetPersist
  };
}
